// The targeting reticle (SPEC §15).
//
// "A targeting reticle that snaps to the hovered pixel."
//
// SNAPS is the word that decides the implementation. The reticle is positioned on
// the pixel's board coordinate converted to CSS pixels, not on the mouse — so at
// close zoom it steps from cell to cell and sits exactly on the grid, and at
// world zoom, where a board pixel is under a device pixel, it holds a minimum
// size so the player can still see what they are about to hit.
//
// It is a DOM overlay rather than a shader effect on purpose: it belongs to the
// interface layer, it has to sit above the arcs, and it is four hairlines. Adding
// it to the map shader would put an interface concern inside the one file §15
// reserves for the war.

import type { ReactNode } from 'react';
import { Stack } from 'tamagui';

export interface ReticleBox {
  /** CSS pixels, relative to the canvas's top-left. */
  left: number;
  top: number;
  size: number;
}

/**
 * Where the reticle goes, in CSS pixels.
 *
 * Pure, and separated from the component, because the arithmetic is the part that
 * is wrong in ways that look like a rendering bug — the camera works in DEVICE
 * pixels and the overlay is laid out in CSS pixels, and the two differ by the
 * device pixel ratio on every machine anyone actually plays on.
 */
export function reticleBox(
  camera: { originX: number; originY: number; scale: number },
  pixel: number,
  boardWidth: number,
  dpr: number,
  minCss = 12,
): ReticleBox {
  const bx = pixel % boardWidth;
  const by = (pixel / boardWidth) | 0;
  const cssScale = camera.scale / (dpr || 1);
  const size = Math.max(minCss, cssScale);
  const left = (bx - camera.originX) * cssScale + (cssScale - size) / 2;
  const top = (by - camera.originY) * cssScale + (cssScale - size) / 2;
  return { left, top, size };
}

/**
 * Four corner ticks and a centre dot. Not a box — a box at close zoom hides the
 * pixel it is pointing at, which is the pixel the player is deciding about.
 */
export function Reticle({ box }: { box: ReticleBox | null }): ReactNode {
  if (!box) return null;
  const arm = Math.max(3, box.size * 0.28);
  const corner = (top: boolean, left: boolean): ReactNode => (
    <Stack
      position="absolute"
      width={arm}
      height={arm}
      top={top ? 0 : undefined}
      bottom={top ? undefined : 0}
      left={left ? 0 : undefined}
      right={left ? undefined : 0}
      borderColor="$cyan"
      borderTopWidth={top ? 1 : 0}
      borderBottomWidth={top ? 0 : 1}
      borderLeftWidth={left ? 1 : 0}
      borderRightWidth={left ? 0 : 1}
    />
  );
  return (
    <Stack
      position="absolute"
      left={box.left}
      top={box.top}
      width={box.size}
      height={box.size}
      pointerEvents="none"
      zIndex="$hud"
    >
      {corner(true, true)}
      {corner(true, false)}
      {corner(false, true)}
      {corner(false, false)}
      <Stack
        position="absolute"
        left="50%"
        top="50%"
        width={1}
        height={1}
        backgroundColor="$cyan"
        opacity={0.8}
      />
    </Stack>
  );
}
